// pages/Dashboard.js
// Dashboard — 4 API calls, summary cards, charts, drill-down, filters
//
// API calls (parallel on mount):
//   GET /dashboard/summary          → summary cards
//   GET /dashboard/unit-scores      → unit comparison bar chart
//   GET /dashboard/category-breakdown?unit_id=X → category bar chart
//   GET /dashboard/alerts-summary   → alerts panel
//
// Drill-down:
//   GET /dashboard/drill-down?category_id=X&unit_id=Y → siblings tree
//
// Filters saved in localStorage: madad_filters_{userId}

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  MenuItem,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

// Icons
import ScoreIcon from '@mui/icons-material/EmojiEventsOutlined';
import MetricIcon from '@mui/icons-material/SpeedOutlined';
import CompletionIcon from '@mui/icons-material/TaskAltOutlined';
import AlertIcon from '@mui/icons-material/WarningAmberOutlined';
import FolderIcon from '@mui/icons-material/FolderOutlined';
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import HomeIcon from '@mui/icons-material/HomeOutlined';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { extractErrorMessage } from '../services/api';
import Loading from '../components/shared/Loading';
import ErrorMessage from '../components/shared/ErrorMessage';

// =============================================================================
// Constants
// =============================================================================
const FILTER_STORAGE_PREFIX = 'madad_filters_';

// Score color scale
function getScoreColor(score) {
  if (score == null) return '#9e9e9e';
  if (score >= 8) return '#4caf50';
  if (score >= 6) return '#ff9800';
  if (score >= 4) return '#f44336';
  return '#b71c1c';
}

// =============================================================================
// Dashboard
// =============================================================================
export default function Dashboard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1199px)');
  const { user } = useAuth();

  const glassCard = theme.custom?.glassmorphism?.card || {};

  // ===================================================================
  // State — API data
  // ===================================================================
  const [summary, setSummary] = useState(null);
  const [unitScores, setUnitScores] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState(null);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [lowScores, setLowScores] = useState(null);
  const [drillDown, setDrillDown] = useState(null);

  // Loading & error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);

  // Filters
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [breadcrumb, setBreadcrumb] = useState([]);

  // ===================================================================
  // Load/Save filters from localStorage
  // ===================================================================
  const filterKey = user?.id ? `${FILTER_STORAGE_PREFIX}${user.id}` : null;

  // Helper: extract valid unit_id from potentially corrupted localStorage
  // Handles: "1", '{"unitId":"1"}', '{"unitId":"{\\"unitId\\":\\"1\\"}"}' etc.
  const _extractUnitId = (raw) => {
    if (!raw) return null;
    // If it's already a plain number string
    if (/^\d+$/.test(String(raw))) return String(raw);
    // Try to extract a number from nested JSON
    const match = String(raw).match(/\d+/);
    return match ? match[0] : null;
  };

  useEffect(() => {
    if (!filterKey) return;
    try {
      const saved = localStorage.getItem(filterKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const unitId = _extractUnitId(parsed.unitId);
        if (unitId) setSelectedUnitId(unitId);
      }
    } catch {
      // Corrupted — clear it
      try { localStorage.removeItem(filterKey); } catch { /* ignore */ }
    }
  }, [filterKey]);

  const saveFilters = useCallback((unitId) => {
    if (!filterKey) return;
    try {
      localStorage.setItem(filterKey, JSON.stringify({ unitId }));
    } catch {
      // ignore
    }
  }, [filterKey]);

  const clearFilters = useCallback(() => {
    setSelectedUnitId('');
    setBreadcrumb([]);
    setDrillDown(null);
    setCategoryBreakdown(null);
    if (filterKey) {
      try { localStorage.removeItem(filterKey); } catch { /* ignore */ }
    }
  }, [filterKey]);

  // ===================================================================
  // Initial data fetch — 5 API calls in parallel
  // ===================================================================
  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError('');

      try {
        const [summaryRes, unitsRes, alertsRes, lowScoresRes] = await Promise.all([
          api.get('/dashboard/summary'),
          api.get('/dashboard/unit-scores'),
          api.get('/dashboard/alerts-summary'),
          api.get('/dashboard/low-scores'),
        ]);

        if (cancelled) return;

        setSummary(summaryRes.data?.data || summaryRes.data);
        const units = summaryRes.data?.data ? (unitsRes.data?.data || []) : (unitsRes.data || []);
        setUnitScores(units);
        setAlertsSummary(alertsRes.data?.data || alertsRes.data);
        setLowScores(lowScoresRes.data?.data || lowScoresRes.data);

        // Auto-select first unit → triggers category-breakdown + breadcrumb
        if (units.length > 0 && !selectedUnitId) {
          let initialUnit = null;
          if (filterKey) {
            try {
              const saved = localStorage.getItem(filterKey);
              if (saved) {
                const parsed = JSON.parse(saved);
                initialUnit = _extractUnitId(parsed.unitId);
              }
            } catch {
              try { localStorage.removeItem(filterKey); } catch { /* ignore */ }
            }
          }
          if (!initialUnit) initialUnit = String(units[0].unit_id);
          setSelectedUnitId(initialUnit);
        }
      } catch (err) {
        if (!cancelled) {
          setError(extractErrorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // ===================================================================
  // Fetch category breakdown when unit selected
  // ===================================================================
  const fetchCategoryBreakdown = useCallback(async (unitId) => {
    if (!unitId) {
      setCategoryBreakdown(null);
      return;
    }
    setCategoryLoading(true);
    try {
      const res = await api.get('/dashboard/category-breakdown', { params: { unit_id: unitId } });
      setCategoryBreakdown(res.data?.data || res.data);
    } catch (err) {
      setCategoryBreakdown(null);
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  // When selectedUnitId changes → fetch categories + low-scores + update breadcrumb
  useEffect(() => {
    if (selectedUnitId) {
      fetchCategoryBreakdown(selectedUnitId);
      // Re-fetch low-scores filtered by unit
      api.get('/dashboard/low-scores', { params: { unit_id: selectedUnitId } })
        .then((res) => setLowScores(res.data?.data || res.data))
        .catch(() => {});
      const unit = unitScores.find((u) => String(u.unit_id) === String(selectedUnitId));
      setBreadcrumb([{ label: unit?.unit_name || '', unitId: selectedUnitId }]);
      setDrillDown(null);
      saveFilters(selectedUnitId);
    } else {
      setCategoryBreakdown(null);
      setBreadcrumb([]);
      setDrillDown(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitId]);

  // ===================================================================
  // Drill-down into category
  // ===================================================================
  const handleDrillDown = useCallback(async (categoryId, categoryName) => {
    if (!selectedUnitId) return;
    setDrillLoading(true);
    try {
      const res = await api.get('/dashboard/drill-down', {
        params: { category_id: categoryId, unit_id: selectedUnitId },
      });
      const data = res.data?.data || res.data;
      setDrillDown(data);
      setBreadcrumb((prev) => [...prev, { label: categoryName, categoryId }]);
    } catch {
      setDrillDown(null);
    } finally {
      setDrillLoading(false);
    }
  }, [selectedUnitId]);

  // Breadcrumb navigation — go back to level
  const handleBreadcrumbClick = useCallback(async (index) => {
    if (index === 0) {
      // Back to unit level — show category breakdown (clear drill-down)
      setDrillDown(null);
      setBreadcrumb((prev) => [prev[0]]);
    } else {
      // Back to intermediate drill-down level
      // Get the categoryId from current breadcrumb before trimming
      const targetCrumb = breadcrumb[index];
      
      // Trim breadcrumb to clicked level (inclusive)
      setBreadcrumb((prev) => prev.slice(0, index + 1));
      
      // Re-fetch drill-down for that category
      if (targetCrumb?.categoryId && selectedUnitId) {
        setDrillLoading(true);
        try {
          const res = await api.get('/dashboard/drill-down', {
            params: { category_id: targetCrumb.categoryId, unit_id: selectedUnitId },
          });
          const data = res.data?.data || res.data;
          setDrillDown(data);
        } catch {
          setDrillDown(null);
        } finally {
          setDrillLoading(false);
        }
      }
    }
  }, [selectedUnitId, breadcrumb]);

  // ===================================================================
  // Handle bar click on unit scores chart
  // ===================================================================
  const handleUnitBarClick = useCallback((data) => {
    if (data?.unit_id) {
      setSelectedUnitId(String(data.unit_id));
    }
  }, []);

  // ===================================================================
  // Summary cards data
  // ===================================================================
  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        key: 'companyScore',
        label: t('dashboard.companyScore'),
        value: summary.company_score != null ? summary.company_score.toFixed(2) : '—',
        icon: ScoreIcon,
        color: getScoreColor(summary.company_score),
      },
      {
        key: 'totalMetrics',
        label: t('dashboard.totalMetrics'),
        value: summary.total_metrics ?? '—',
        icon: MetricIcon,
        color: theme.palette.info.main,
      },
      {
        key: 'completion',
        label: t('dashboard.completion'),
        value: summary.completion?.rate != null ? `${summary.completion.rate.toFixed(1)}%` : '—',
        icon: CompletionIcon,
        color: theme.palette.success.main,
      },
      {
        key: 'openAlerts',
        label: t('dashboard.openAlerts'),
        value: summary.open_alerts ?? '—',
        icon: AlertIcon,
        color: summary.open_alerts > 0 ? theme.palette.error.main : theme.palette.success.main,
      },
    ];
  }, [summary, theme, t]);

  // ===================================================================
  // Sorted unit scores for chart
  // ===================================================================
  const sortedUnitScores = useMemo(() => {
    return [...unitScores].sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [unitScores]);

  // ===================================================================
  // Render — Loading / Error
  // ===================================================================
  if (loading) {
    return <Loading fullPage message={t('dashboard.loading')} />;
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <ErrorMessage error={error} onRetry={() => window.location.reload()} />
      </Box>
    );
  }

  // ===================================================================
  // Render — Dashboard content
  // ===================================================================
  return (
    <Box data-testid="dashboard__page" sx={{ pb: 4 }}>

      {/* ============================================================= */}
      {/* Breadcrumb                                                     */}
      {/* ============================================================= */}
      {breadcrumb.length > 0 && (
        <Breadcrumbs
          data-testid="dashboard__breadcrumb"
          separator={<NavigateBeforeIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={clearFilters}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon fontSize="small" />
            {t('dashboard.company')}
          </Link>
          {breadcrumb.map((crumb, index) => {
            const isLast = index === breadcrumb.length - 1;
            return isLast ? (
              <Typography key={index} color="text.primary" fontWeight={600}>
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                component="button"
                underline="hover"
                color="inherit"
                onClick={() => handleBreadcrumbClick(index)}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      {/* ============================================================= */}
      {/* Summary Cards                                                  */}
      {/* ============================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Grid item xs={6} sm={6} md={3} key={card.key}>
              <Paper
                data-testid={`dashboard__card--${card.key}`}
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  ...glassCard,
                  borderInlineStart: `4px solid ${card.color}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {card.label}
                  </Typography>
                  <Icon sx={{ fontSize: 20, color: card.color, opacity: 0.8 }} />
                </Box>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ fontFamily: "'IBM Plex Mono', monospace", color: card.color }}
                >
                  {card.value}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* ============================================================= */}
      {/* Filters row                                                    */}
      {/* ============================================================= */}
      <Box
        data-testid="dashboard__filters"
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}
      >
        <TextField
          data-testid="dashboard__select--unit-filter"
          select
          size="small"
          label={t('dashboard.filterByUnit')}
          value={selectedUnitId}
          onChange={(e) => setSelectedUnitId(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">
            <em>{t('dashboard.allUnits')}</em>
          </MenuItem>
          {unitScores.map((u) => (
            <MenuItem key={u.unit_id} value={String(u.unit_id)}>
              {u.unit_name}
            </MenuItem>
          ))}
        </TextField>

        {selectedUnitId && (
          <Button
            data-testid="dashboard__button--clear-filters"
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={clearFilters}
            color="inherit"
          >
            {t('dashboard.clearFilters')}
          </Button>
        )}
      </Box>

      {/* ============================================================= */}
      {/* Charts row                                                     */}
      {/* ============================================================= */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Unit Scores Bar Chart */}
        <Grid item xs={12} md={drillDown || categoryBreakdown ? 6 : 12}>
          <Paper
            data-testid="dashboard__chart--unit-scores"
            elevation={0}
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, ...glassCard }}
          >
            <Typography variant="h6" gutterBottom fontWeight={600}>
              {t('dashboard.unitScoresChart')}
            </Typography>

            {sortedUnitScores.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, sortedUnitScores.length * 45 + 40)}>
                <BarChart
                  data={sortedUnitScores}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" domain={[0, 10]} tickCount={6} />
                  <YAxis
                    type="category"
                    dataKey="unit_name"
                    width={100}
                    tick={{ fontSize: 13 }}
                  />
                  <RechartsTooltip
                    formatter={(value) => [value?.toFixed(2), t('dashboard.score')]}
                    contentStyle={{
                      borderRadius: 8,
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Bar
                    dataKey="score"
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(data) => handleUnitBarClick(data)}
                  >
                    {sortedUnitScores.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={getScoreColor(entry.score)}
                        opacity={selectedUnitId && String(entry.unit_id) !== String(selectedUnitId) ? 0.35 : 1}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>
                {t('emptyStates.noData')}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Category Breakdown / Drill-Down */}
        {(categoryBreakdown || drillDown) && (
          <Grid item xs={12} md={6}>
            <Paper
              data-testid="dashboard__chart--category-breakdown"
              elevation={0}
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, ...glassCard }}
            >
              {drillDown ? (
                /* Drill-down siblings view */
                <DrillDownPanel
                  data={drillDown}
                  loading={drillLoading}
                  onDrillDeeper={handleDrillDown}
                  t={t}
                />
              ) : categoryLoading ? (
                <Box>
                  <Skeleton variant="text" width="40%" height={32} />
                  {[1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />)}
                </Box>
              ) : (
                /* Category breakdown chart */
                <CategoryBreakdownPanel
                  data={categoryBreakdown}
                  onCategoryClick={handleDrillDown}
                  t={t}
                  theme={theme}
                />
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* ============================================================= */}
      {/* Low Scores Panel                                               */}
      {/* ============================================================= */}
      {lowScores && (
        <Paper
          data-testid="dashboard__panel--low-scores"
          elevation={0}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3, ...glassCard }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AlertIcon sx={{ color: theme.palette.warning.main }} />
            <Typography variant="h6" fontWeight={600}>
              {t('dashboard.lowScores')}
            </Typography>
            <Chip
              size="small"
              label={`${t('dashboard.threshold')}: ${lowScores.threshold ?? 5}`}
              variant="outlined"
              color="warning"
            />
          </Box>

          {lowScores.metrics && lowScores.metrics.length > 0 ? (
            <List disablePadding>
              {lowScores.metrics.map((metric) => (
                <ListItem key={metric.metric_id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    data-testid={`dashboard__low-score--${metric.metric_id}`}
                    sx={{ borderRadius: 1, py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <BarChartIcon fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={metric.metric_name}
                      secondary={metric.unit_name}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      label={metric.score?.toFixed(2)}
                      size="small"
                      sx={{
                        bgcolor: getScoreColor(metric.score),
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CompletionIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.noLowScores')}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* ============================================================= */}
      {/* Alerts Summary                                                 */}
      {/* ============================================================= */}
      {alertsSummary && (
        <Paper
          data-testid="dashboard__panel--alerts"
          elevation={0}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, ...glassCard }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              {t('dashboard.alertsSummary')}
            </Typography>
            {alertsSummary.by_type && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  size="small"
                  label={`${t('dashboard.alertTypes.deadline')}: ${alertsSummary.by_type.deadline || 0}`}
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`${t('dashboard.alertTypes.low_score')}: ${alertsSummary.by_type.low_score || 0}`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`${t('dashboard.alertTypes.missing_data')}: ${alertsSummary.by_type.missing_data || 0}`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>

          {alertsSummary.recent && alertsSummary.recent.length > 0 ? (
            <List disablePadding>
              {alertsSummary.recent.map((alert) => (
                <ListItem key={alert.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    data-testid={`dashboard__alert--${alert.id}`}
                    sx={{ borderRadius: 1, py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AlertIcon
                        fontSize="small"
                        color={
                          alert.alert_type === 'low_score' ? 'error'
                            : alert.alert_type === 'deadline' ? 'warning'
                              : 'info'
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.alert_message}
                      secondary={`${alert.unit_name || ''} ${alert.metric_name ? '/ ' + alert.metric_name : ''}`}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
              {t('notifications.noNotifications')}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

// =============================================================================
// CategoryBreakdownPanel — sub-component
// =============================================================================
function CategoryBreakdownPanel({ data, onCategoryClick, t, theme }) {
  const categories = data?.categories || [];

  if (categories.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>
        {t('emptyStates.noData')}
      </Typography>
    );
  }

  // Color palette for pie chart
  const COLORS = ['#1565c0', '#e91e63', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];

  return (
    <>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {t('dashboard.categoryBreakdown')}
      </Typography>

      {/* Pie chart */}
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={categories}
            dataKey="contribution"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
          >
            {categories.map((_, idx) => (
              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Pie>
          <RechartsTooltip
            formatter={(value, name) => [value?.toFixed(2), name]}
            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Category list — clickable for drill-down */}
      <List disablePadding sx={{ mt: 1 }}>
        {categories.map((cat, idx) => (
          <ListItem key={cat.category_id} disablePadding>
            <ListItemButton
              data-testid={`dashboard__category--${cat.category_id}`}
              onClick={() => onCategoryClick(cat.category_id, cat.category_name)}
              sx={{ borderRadius: 1, py: 0.75 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: COLORS[idx % COLORS.length],
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={cat.category_name}
                primaryTypographyProps={{ variant: 'body2' }}
              />
              <Chip
                label={cat.score?.toFixed(1)}
                size="small"
                sx={{
                  bgcolor: getScoreColor(cat.score),
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );
}

// =============================================================================
// DrillDownPanel — sub-component
// =============================================================================
function DrillDownPanel({ data, loading, onDrillDeeper, t }) {
  const details = data?.details || [];

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="50%" height={32} />
        {[1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />)}
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {data?.category_name || t('dashboard.drillDown')}
      </Typography>

      {details.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>
          {t('emptyStates.noData')}
        </Typography>
      ) : (
        <List disablePadding>
          {details.map((item) => {
            const isCategory = item.type === 'category';
            return (
              <Box key={`${item.type}-${item.id}`}>
                <ListItem disablePadding>
                  <ListItemButton
                    data-testid={`dashboard__drill-item--${item.type}-${item.id}`}
                    onClick={isCategory ? () => onDrillDeeper(item.id, item.name) : undefined}
                    sx={{
                      borderRadius: 1,
                      py: 1,
                      cursor: isCategory ? 'pointer' : 'default',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {isCategory ? (
                        <FolderIcon fontSize="small" color="primary" />
                      ) : (
                        <BarChartIcon fontSize="small" color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      secondary={`${t('dashboard.weight')}: ${item.weight?.toFixed(1)}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: isCategory ? 600 : 400 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      label={item.score?.toFixed(1)}
                      size="small"
                      sx={{
                        bgcolor: getScoreColor(item.score),
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                {/* Children (metrics under a category) */}
                {isCategory && item.children && item.children.length > 0 && (
                  <List disablePadding sx={{ pl: 4 }}>
                    {item.children.map((child) => (
                      <ListItem key={`${child.type}-${child.id}`} disablePadding>
                        <ListItemButton
                          data-testid={`dashboard__drill-item--${child.type}-${child.id}`}
                          sx={{ borderRadius: 1, py: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <BarChartIcon fontSize="small" sx={{ opacity: 0.5 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={child.name}
                            secondary={`${t('dashboard.weight')}: ${child.weight?.toFixed(1)}`}
                            primaryTypographyProps={{ variant: 'caption' }}
                            secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }}
                          />
                          <Chip
                            label={child.score?.toFixed(1)}
                            size="small"
                            sx={{
                              bgcolor: getScoreColor(child.score),
                              color: '#fff',
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            );
          })}
        </List>
      )}
    </>
  );
}
